import { BarChart3, Calendar, ClipboardList, Dumbbell, Menu, Scale } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useUpdateEnabledSections } from '../api/hooks/useSettings'
import { useSectionReorder } from '../hooks/useSectionReorder'
import { useLoadlineIOSPromo } from '../hooks/useLoadlineIOSPromo'
import { DISCORD_INVITE_URL, LOADLINE_IOS_APP_URL } from '../lib/constants'
import { useUIStore } from '../stores/uiStore'
import { DashboardSection } from '../types'
import { LoadlineIOSPromoModal, LoadlineIOSPromoSidebarCard } from './LoadlineIOSPromo'
import { Sidebar } from './Sidebar'
import { UnifiedButton } from './ui/unified-button'

// Move DASHBOARD_SECTIONS outside component to avoid stale closure
const DASHBOARD_SECTIONS = [
  {
    id: 'calendar' as const,
    label: 'Calendar',
    icon: Calendar,
    description: 'Workout calendar and schedule'
  },
  {
    id: 'weight' as const,
    label: 'Weight',
    icon: Scale,
    description: 'Weight tracking and trends'
  },
  {
    id: 'routine' as const,
    label: 'Training Split',
    icon: ClipboardList,
    description: 'Workout routine configuration'
  },
  {
    id: 'weeklyStats' as const,
    label: 'Weekly Sets',
    icon: BarChart3,
    description: 'Weekly muscle group statistics'
  },
  {
    id: 'muscleGroups' as const,
    label: 'Muscle Groups',
    icon: Dumbbell,
    description: 'Charts and progressive overload index'
  }
]

export const Layout = () => {
  const location = useLocation()
  const isDashboard = location.pathname === '/'
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)
  const { isModalOpen, isSidebarVisible, setModalOpen, markModalSeen, openModal } =
    useLoadlineIOSPromo()

  // Drag reordering with local state management
  const { currentItems, reorder } = useSectionReorder()
  const {
    resetSections,
    removeSection,
    collapsedSections,
    toggleSection,
    isDragging,
    setDragging,
    setScrollTarget
  } = useUIStore()
  const updateEnabledSections = useUpdateEnabledSections()

  const orderedSections = useMemo(() => {
    const sectionMap = new Map(DASHBOARD_SECTIONS.map((section) => [section.id, section]))

    // Use currentItems from reorder hook (includes local drag state)
    return currentItems.map((id) => sectionMap.get(id as DashboardSection)!).filter(Boolean)
  }, [currentItems])

  useEffect(() => {
    setIsMobileSidebarOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileSidebarOpen(false)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [])

  const openExternalLink = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [])

  const handlePromoModalChange = useCallback(
    (open: boolean) => {
      setModalOpen(open)
      if (!open) {
        markModalSeen()
      }
    },
    [markModalSeen, setModalOpen]
  )

  const handlePromoPrimaryAction = useCallback(() => {
    markModalSeen()
    setModalOpen(false)
    openExternalLink(LOADLINE_IOS_APP_URL)
  }, [markModalSeen, openExternalLink, setModalOpen])

  const handlePromoDiscordAction = useCallback(() => {
    markModalSeen()
    setModalOpen(false)
    openExternalLink(DISCORD_INVITE_URL)
  }, [markModalSeen, openExternalLink, setModalOpen])

  return (
    <div className="relative flex h-[100dvh] overflow-hidden">
      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Let the Sidebar component handle its own positioning */}
      <Sidebar
        collapsedSections={collapsedSections}
        onToggleSection={(section: DashboardSection) => toggleSection(section)}
        onScrollToSection={(sectionId: string) => {
          if (isDashboard) {
            setScrollTarget(sectionId)
          }
        }}
        orderedSections={isDashboard ? orderedSections : []}
        isDragging={isDragging}
        onDragStart={() => setDragging(true)}
        onDragEnd={(activeId: string, overId: string) => {
          setDragging(false)
          reorder(activeId, overId)
        }}
        onResetOrder={() => {
          // Immediately update Zustand state for instant UI feedback
          resetSections()

          // Background persistence to database
          updateEnabledSections.mutate([
            'calendar',
            'weight',
            'routine',
            'weeklyStats',
            'muscleGroups'
          ])
        }}
        onRemoveSection={(sectionId: DashboardSection) => {
          // Immediately update Zustand state for instant UI feedback
          removeSection(sectionId)

          // Background persistence to database
          const newSections = currentItems.filter((id) => id !== sectionId)
          updateEnabledSections.mutate(newSections)
        }}
        showDashboardSections={isDashboard}
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={() => setIsMobileSidebarOpen(false)}
        sectionsLoading={false}
        hideDiscord={isSidebarVisible}
        bottomSlot={
          isSidebarVisible ? <LoadlineIOSPromoSidebarCard onOpenDetails={openModal} /> : null
        }
      />

      <LoadlineIOSPromoModal
        open={isModalOpen}
        onOpenChange={handlePromoModalChange}
        onPrimaryAction={handlePromoPrimaryAction}
        onDiscordAction={handlePromoDiscordAction}
      />

      {/* Main Content */}
      <div className="flex h-full flex-1 flex-col overflow-hidden transition-all duration-300 lg:ml-64">
        {/* Mobile Menu Button */}
        <div className="bg-background sticky top-0 z-30 border-b px-4 py-2 lg:hidden">
          <UnifiedButton
            variant="ghost"
            size="sm"
            onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
          >
            <Menu className="h-5 w-5" />
          </UnifiedButton>
        </div>

        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
